import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { CustomButton } from '../../../components/common/customButton';
import { CustomTextInput } from '../../../components/common/inputFields/customTextInput';
import { CustomMulitLineTextField } from '../../../components/common/inputFields/customMultiLineTextFIeld';
import { createOrUpdatePrompt, updatePrompt, type Prompt, type CreateOrUpdatePromptRequest } from '../../../services/promptsApi';
import { useErrorDialog } from '../../../hooks/useErrorDialog';
import { ErrorDialog } from '../../../components/common/errorDialog';
import '../../../assets/css/Text-General.css';

interface PromptEditorProps {
  prompt: Prompt | null;
  onSaveSuccess: () => void;
  onCancel: () => void;
}

export const PromptEditor = ({ prompt, onSaveSuccess, onCancel }: PromptEditorProps) => {
  const [formData, setFormData] = useState<CreateOrUpdatePromptRequest>({
    prompt_key: '',
    prompt_name: '',
    description: null,
    prompt_content: '',
  });
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<number | null>(null);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  useEffect(() => {
    if (prompt) {
      setFormData({
        prompt_key: prompt.prompt_key,
        prompt_name: prompt.prompt_name,
        description: prompt.description,
        prompt_content: prompt.prompt_content,
      });
      setCurrentVersion(prompt.version);
    } else {
      // Reset form for new prompt
      setFormData({
        prompt_key: '',
        prompt_name: '',
        description: null,
        prompt_content: '',
      });
      setCurrentVersion(null);
    }
  }, [prompt]);

  const handleInputChange = (field: keyof CreateOrUpdatePromptRequest, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value === '' ? null : value,
    }));
  };

  const validateForm = (): string | null => {
    // handleInputChange stores cleared fields as null, so guard before trimming.
    if (!formData.prompt_key?.trim()) {
      return 'Prompt key is required';
    }
    if (!formData.prompt_name?.trim()) {
      return 'Prompt name is required';
    }
    if (!formData.prompt_content?.trim()) {
      return 'Prompt content is required';
    }
    // Validate prompt_key format (should be lowercase with dots/underscores)
    if (!/^[a-z0-9_.]+$/.test(formData.prompt_key)) {
      return 'Prompt key must be lowercase and contain only letters, numbers, dots, and underscores';
    }
    return null;
  };

  const handleSaveClick = () => {
    const validationError = validateForm();
    if (validationError) {
      showErrorFromResponse({
        success: false,
        status: 400,
        message: validationError,
      });
      return;
    }

    if (prompt && currentVersion) {
      // Show confirmation dialog for updates (creates new version)
      setConfirmDialogOpen(true);
    } else {
      // Create new prompt - no confirmation needed
      handleSave();
    }
  };

  const handleSave = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);

    try {
      let response;
      if (prompt) {
        // Update existing prompt (creates new version)
        response = await updatePrompt(prompt.prompt_key, formData);
      } else {
        // Create new prompt
        response = await createOrUpdatePrompt(formData);
      }

      if (response.success) {
        onSaveSuccess();
      } else {
        showErrorFromResponse(response);
      }
    } catch (error) {
      showErrorFromResponse({
        success: false,
        status: 500,
        message: `Unexpected error: ${error}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormDirty = () => {
    if (!prompt) {
      return formData.prompt_key || formData.prompt_name || formData.prompt_content;
    }
    return (
      formData.prompt_key !== prompt.prompt_key ||
      formData.prompt_name !== prompt.prompt_name ||
      formData.description !== prompt.description ||
      formData.prompt_content !== prompt.prompt_content
    );
  };

  return (
    <Box>
      <Box sx={{ mb: '10px', mt: 1, px: 3 }}>
        <Typography variant="h6">
          {prompt ? 'Edit Prompt' : 'Create New Prompt'}
        </Typography>
        {prompt && currentVersion && (
          <Typography variant="body2" color="text.secondary">
            Current Version: {currentVersion} • This will create version {currentVersion + 1}
          </Typography>
        )}
      </Box>

      <Paper elevation={1} sx={{ px: 3, pb: 3, pt: '10px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <CustomTextInput
              label="Prompt Key"
              value={formData.prompt_key}
              onChange={(val) => typeof val === 'string' && handleInputChange('prompt_key', val)}
              disabled={!!prompt} // Read-only when editing existing
              margin="none"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Unique identifier (e.g., 'text_summary.email'). Must be lowercase with dots/underscores.
            </Typography>
          </Box>

          <Box>
            <CustomTextInput
              label="Prompt Name"
              value={formData.prompt_name}
              onChange={(val) => typeof val === 'string' && handleInputChange('prompt_name', val)}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Human-readable name for this prompt
            </Typography>
          </Box>

          <Box>
            <CustomMulitLineTextField
              label="Description"
              value={formData.description || ''}
              onChange={(val) => handleInputChange('description', typeof val === 'string' ? val : '')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Optional description of what this prompt is used for
            </Typography>
          </Box>

          <Box>
            <CustomMulitLineTextField
              label="Prompt Content"
              value={formData.prompt_content}
              onChange={(val) => handleInputChange('prompt_content', typeof val === 'string' ? val : '')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              The actual prompt text. Supports placeholders like {'{file_list}'}, {'{reference_lists}'}, etc.
            </Typography>
          </Box>

          {prompt && (
            <Alert severity="warning">
              <Typography variant="body2">
                <strong>Note:</strong> Saving this prompt will create a new version ({currentVersion ? currentVersion + 1 : 1}) and
                automatically deactivate the current version ({currentVersion}).
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
            <CustomButton
              handleButtonPress={onCancel}
              isButtonDisabled={loading}
              text="Cancel"
              solid={false}
            />
            <CustomButton
              handleButtonPress={handleSaveClick}
              isButtonDisabled={loading || !isFormDirty()}
              text={loading ? 'Saving...' : prompt ? 'Save (New Version)' : 'Create Prompt'}
              solid={true}
            />
          </Box>
        </Box>
      </Paper>

      {/* Confirmation Dialog for Updates */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Create New Version?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will create version {currentVersion ? currentVersion + 1 : 1} of this prompt and deactivate version{' '}
            {currentVersion}. The old version will remain in the history but will no longer be active.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CustomButton
            handleButtonPress={() => setConfirmDialogOpen(false)}
            isButtonDisabled={false}
            text="Cancel"
            solid={false}
          />
          <CustomButton
            handleButtonPress={handleSave}
            isButtonDisabled={false}
            text="Create New Version"
            solid={true}
          />
        </DialogActions>
      </Dialog>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

